import type { JSX } from 'react';
import {
  BarChart3,
  Briefcase,
  CheckSquare,
  FileText,
  Folder,
  LayoutDashboard,
  Settings,
  Users,
} from 'lucide-react';
import { cn } from '@lib/cn';

interface HeroMockupPlaceholderProps {
  readonly className?: string;
  readonly ariaLabel: string;
}

// Dados ficticios — meramente decorativos. Estes nao sao "copy" da landing
// (nao representam valores reais da Omanna), sao ornamentos visuais para o
// mockup do dashboard. Quando Lucas anexar o screenshot real, esse componente
// inteiro e substituido por <img src="/hero-mockup.webp" />.
const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', active: true },
  { icon: Users, label: 'Clientes', active: false },
  { icon: Folder, label: 'Projetos', active: false },
  { icon: CheckSquare, label: 'Tarefas', active: false },
  { icon: Briefcase, label: 'Financeiro', active: false },
  { icon: BarChart3, label: 'Relatórios', active: false },
  { icon: Settings, label: 'Configurações', active: false },
] as const;

const KPIS = [
  { label: 'Projetos ativos', value: '24', delta: '+12% este mês' },
  { label: 'Tarefas concluídas', value: '153', delta: '+18% este mês' },
  { label: 'Receita mensal', value: 'R$ 48.750', delta: '+22% este mês' },
] as const;

const CHART_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul'] as const;
// Polyline de receita ficticia — sobe gradualmente com 1 pequena correcao em Abr
const CHART_POINTS = '0,55 32,42 64,28 96,38 128,22 160,15 192,8';

const ACTIVITIES = [
  { name: 'Projeto Website', timeAgo: 'Atualizado há 2h' },
  { name: 'Sistema de Vendas', timeAgo: 'Atualizado há 4h' },
  { name: 'App Cliente', timeAgo: 'Atualizado há 5h' },
] as const;

/**
 * Phase 1 placeholder do mockup do dashboard Omanna.
 *
 * Renderiza um laptop estilizado com um dashboard fake dentro — replica
 * visual proxima do design-reference do Lucas (.planning/design-reference.md
 * "Hero mockup target"). Zero asset, ~5 KB de DOM. Quando Lucas anexar o
 * screenshot real (PRD §14 item 3), substitui esse componente por
 * `<img src="/hero-mockup.webp" alt={ariaLabel} />` em uma linha no Hero.tsx.
 *
 * Estrutura:
 *  - Wrapper com `perspective` e `rotate-y` (efeito 3D sutil — laptop levemente
 *    angulado pra direita, como na peca do Lucas)
 *  - Moldura do laptop: bg-neutral-800 com ring + shadow-2xl
 *  - Tela: bg-brand-900 (dashboard dark-green) com dashboard interno
 *  - Dashboard: sidebar (logo + menu lucide) + main (3 KPIs + chart SVG + lista
 *    de atividades)
 *  - Base do laptop: barra fina cinza com indentacao do trackpad
 *
 * Acessibilidade:
 *  - `role="img"` + `aria-label` descritivo (mockupAlt do landingCopy)
 *  - Todos os elementos internos sao `aria-hidden="true"` (decorativos)
 *  - Leitores de tela anunciam um unico label coerente, nao tentam ler
 *    individualmente "24", "Projetos ativos", etc (seria ruido)
 *
 * Efeito: hover do mockup amplia 1.01 e desangula levemente — micro
 * interactivity sem custo de animacao lib (matches PRD §7.4).
 */
export const HeroMockupPlaceholder = ({
  className,
  ariaLabel,
}: HeroMockupPlaceholderProps): JSX.Element => (
  <div
    role="img"
    aria-label={ariaLabel}
    className={cn('relative w-full max-w-[640px]', className)}
    style={{ perspective: '1600px' }}
  >
    <div
      aria-hidden="true"
      className="origin-bottom transform-gpu transition-transform duration-700 ease-out hover:[transform:perspective(1600px)_rotateY(-6deg)_rotateX(3deg)] motion-reduce:transition-none [transform:perspective(1600px)_rotateY(-10deg)_rotateX(5deg)]"
    >
      {/* === SCREEN === */}
      <div className="relative rounded-[1.5rem] bg-neutral-800 p-2.5 shadow-2xl ring-1 ring-neutral-700 sm:p-3">
        {/* Camera notch */}
        <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-12 -translate-x-1/2 rounded-full bg-neutral-900" />

        {/* Display — light mode dashboard */}
        <div className="relative aspect-[16/10] overflow-hidden rounded-lg bg-neutral-100">
          <div className="flex h-full">
            {/* === SIDEBAR — branco com borda direita === */}
            <aside className="hidden h-full w-[28%] shrink-0 border-r border-neutral-200 bg-white p-3 sm:flex sm:flex-col lg:w-[26%] lg:p-4">
              <div className="mb-4 flex items-center gap-1.5 lg:mb-6 lg:gap-2">
                <svg
                  viewBox="0 0 24 32"
                  className="h-4 w-3 shrink-0 text-brand-600 lg:h-5 lg:w-[0.9375rem]"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable={false}
                >
                  <path d="M12 0c-.55 0-1.07.27-1.39.71C8.36 3.71 0 14.94 0 20.5 0 26.85 5.37 32 12 32s12-5.15 12-11.5C24 14.94 15.64 3.71 13.39.71 13.07.27 12.55 0 12 0Zm-3 16.5c.55 0 1 .56 1 1.25S9.55 19 9 19s-1-.56-1-1.25.45-1.25 1-1.25Z" />
                </svg>
                <span className="font-display text-[11px] font-bold tracking-tight text-neutral-900 lg:text-[13px]">
                  omanna
                </span>
              </div>

              <nav className="flex flex-col gap-0.5 lg:gap-1">
                {SIDEBAR_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className={cn(
                        'flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[9px] lg:gap-2 lg:px-2 lg:py-1.5 lg:text-[10px]',
                        item.active
                          ? 'bg-brand-50 font-semibold text-brand-700'
                          : 'text-neutral-500',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-2.5 w-2.5 shrink-0 lg:h-3 lg:w-3',
                          item.active ? 'text-brand-600' : 'text-neutral-400',
                        )}
                        strokeWidth={2}
                      />
                      <span className="truncate">{item.label}</span>
                    </div>
                  );
                })}
              </nav>
            </aside>

            {/* === MAIN === */}
            <main className="flex flex-1 flex-col gap-2 p-3 sm:gap-3 sm:p-4 lg:gap-3 lg:p-5">
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 lg:gap-3">
                {KPIS.map((kpi) => (
                  <div
                    key={kpi.label}
                    className="rounded-md bg-white p-2 ring-1 ring-neutral-200 lg:rounded-lg lg:p-3"
                  >
                    <p className="text-[7px] uppercase tracking-wider text-neutral-400 sm:text-[8px] lg:text-[9px]">
                      {kpi.label}
                    </p>
                    <p className="mt-0.5 font-display text-xs font-bold text-neutral-900 sm:text-sm lg:mt-1 lg:text-base">
                      {kpi.value}
                    </p>
                    <p className="mt-0.5 text-[6px] text-brand-600 sm:text-[7px] lg:text-[9px]">
                      {kpi.delta}
                    </p>
                  </div>
                ))}
              </div>

              {/* Chart + Activities row */}
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[3fr_2fr] sm:gap-3">
                {/* Chart */}
                <div className="rounded-md bg-white p-2 ring-1 ring-neutral-200 lg:rounded-lg lg:p-3">
                  <p className="mb-1 text-[8px] font-medium text-neutral-500 sm:text-[9px] lg:mb-2 lg:text-[10px]">
                    Resumo de projetos
                  </p>
                  <svg
                    viewBox="0 0 192 70"
                    preserveAspectRatio="none"
                    className="h-12 w-full sm:h-16 lg:h-20"
                  >
                    <line
                      x1="0" y1="35" x2="192" y2="35"
                      stroke="#e5e7eb"
                      strokeWidth="0.5"
                      strokeDasharray="2,2"
                    />
                    <polygon
                      points={`${CHART_POINTS} 192,70 0,70`}
                      fill="url(#omanna-chart-gradient)"
                    />
                    <polyline
                      points={CHART_POINTS}
                      fill="none"
                      stroke="#1D9E75"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {CHART_POINTS.split(' ').map((point) => {
                      const [x, y] = point.split(',');
                      return (
                        <circle
                          key={point}
                          cx={x}
                          cy={y}
                          r="1.5"
                          fill="#1D9E75"
                          stroke="#fff"
                          strokeWidth="0.8"
                        />
                      );
                    })}
                    <defs>
                      <linearGradient id="omanna-chart-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1D9E75" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#1D9E75" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="mt-1 flex justify-between text-[6px] text-neutral-400 sm:text-[7px] lg:text-[8px]">
                    {CHART_LABELS.map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                  </div>
                </div>

                {/* Activities */}
                <div className="rounded-md bg-white p-2 ring-1 ring-neutral-200 lg:rounded-lg lg:p-3">
                  <p className="mb-1 text-[8px] font-medium text-neutral-500 sm:text-[9px] lg:mb-2 lg:text-[10px]">
                    Atividades recentes
                  </p>
                  <ul className="space-y-1 lg:space-y-1.5">
                    {ACTIVITIES.map((activity) => (
                      <li key={activity.name} className="flex items-center gap-1.5 lg:gap-2">
                        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded bg-brand-50 ring-1 ring-brand-100 lg:h-5 lg:w-5">
                          <FileText
                            className="h-2 w-2 text-brand-600 lg:h-2.5 lg:w-2.5"
                            strokeWidth={2}
                          />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[8px] font-medium text-neutral-800 sm:text-[9px] lg:text-[10px]">
                            {activity.name}
                          </p>
                          <p className="text-[6px] text-neutral-400 sm:text-[7px] lg:text-[8px]">
                            {activity.timeAgo}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* === LAPTOP BASE === */}
      <div className="relative mx-auto -mt-px h-2 w-[103%] -translate-x-[1.5%] rounded-b-lg bg-gradient-to-b from-neutral-300 via-neutral-400 to-neutral-500 shadow-lg">
        {/* Trackpad indent */}
        <div className="absolute left-1/2 top-0.5 h-0.5 w-16 -translate-x-1/2 rounded-full bg-neutral-500/50" />
      </div>
      <div className="mx-auto h-1 w-[80%] rounded-b-2xl bg-neutral-400/60 shadow-lg" />
    </div>
  </div>
);
